/**
 * Properties List View
 * 
 * Main page for displaying and managing channel properties (Units Management).
 * Uses useGetMsoolUnits hook to fetch properties data.
 */

import { FC, useCallback, useMemo, useState, memo, useRef, useEffect } from 'react';
import { useToast } from '@context/ToastContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, MenuItem, Skeleton, Divider, Typography } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { AddOutlined } from '@mui/icons-material';
import HomeIcon from '@assets/svg/design-system/home.svg?react';
import LinkedIcon from '@assets/svg/design-system/linked-property.svg?react';
import CalendarIcon from '@assets/svg/design-system/calendar.svg?react';

import { useGetMsoolUnits } from '../../services/mapping.service';
import { useGetAirbnbUnitsQuery, useGetBookingUnitsQuery, useGetGathernUnitsQuery } from '../../services/channels.service';
import { IMsoolUnit, ChannelType } from '../../types';
import { PageContainer, HeaderSection, PageTitle, PageDescription } from '../../styles';
import { T_KEYS } from '../../constants';

// Import components
import PropertiesEmptyState from '../../components/PropertiesEmptyState';
import PropertiesTable from '../../components/PropertiesTable';
import ChannelToggleModal from '../../components/ChannelToggleModal';
import SelectChannelModal from '../../components/SelectChannelModal';
import SelectUnitsModal from '../../components/SelectUnitsModal';
import NoNewUnitsModal from '../../components/NoNewUnitsModal';
import UnmergeOptionsModal from '../../components/UnmergeOptionsModal';

// Menu styled components
const MenuTitle = muiStyled(Typography)({
  fontWeight: 600,
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
  fontSize: '14px',
  color: '#1a1a1a',
});

const MenuActionItem = muiStyled(MenuItem)({
  color: '#5A39D7',
  borderRadius: 1,
});

// Constants
const ITEMS_PER_PAGE = 10;

// Styled Components
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;



const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #e9e9e7;
`;

const ResultsInfo = styled.span`
  font-family: 'Tajawal', sans-serif;
  font-size: 14px;
  color: #697586;
`;

const LoadingContainer = styled.div`
  padding: 24px;
`;



// Main Component
const PropertiesListView: FC = () => {
  const navigate = useNavigate();
  const { t, i18n: { language } } = useTranslation();
  const { showToast } = useToast();
  const isRtl = language === 'ar';

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Debounce search query for performance (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedProperty, setSelectedProperty] = useState<IMsoolUnit | null>(null);
  const [showChannelToggleModal, setShowChannelToggleModal] = useState(false);
  const [showSelectChannelModal, setShowSelectChannelModal] = useState(false);
  const [showSelectUnitsModal, setShowSelectUnitsModal] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<ChannelType | null>(null);
  const [showNoNewUnitsModal, setShowNoNewUnitsModal] = useState(false);
  const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
  const [showUnmergeModal, setShowUnmergeModal] = useState(false);

  // Fetch data
  const { data, isLoading, isError } = useGetMsoolUnits();
  
  // Fetch channel units for counting unmerged
  const { data: airbnbData } = useGetAirbnbUnitsQuery();
  const { data: bookingData } = useGetBookingUnitsQuery();
  const { data: gathernData } = useGetGathernUnitsQuery();
  
  // Calculate unmerged counts
  const unmergedCounts = useMemo(() => ({
    airbnb: airbnbData?.units?.filter(u => !u.isMapped).length ?? 0,
    booking: bookingData?.units?.filter(u => !u.isMapped).length ?? 0,
    gathern: gathernData?.units?.filter(u => !u.isMapped).length ?? 0,
  }), [airbnbData, bookingData, gathernData]);
  
  // Get units for selected channel
  const selectedChannelUnits = useMemo(() => {
    if (!selectedChannelType) return [];
    switch (selectedChannelType) {
      case ChannelType.Airbnb:
        return airbnbData?.units ?? [];
      case ChannelType.Booking:
        return bookingData?.units ?? [];
      case ChannelType.Gathern:
        return gathernData?.units ?? [];
      default:
        return [];
    }
  }, [selectedChannelType, airbnbData, bookingData, gathernData]);

  // Computed values
  const properties = useMemo(() => data?.units || [], [data]);
  
  const filteredProperties = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return properties;
    const query = debouncedSearchQuery.toLowerCase();
    return properties.filter((p) => 
      (p.propertyName ?? '').toLowerCase().includes(query)
    );
  }, [properties, debouncedSearchQuery]);

  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProperties.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProperties, currentPage]);

  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);

  // Stats
  const stats = useMemo(() => {
    // Number of Properties: count all units from all channels (regardless of status)
    const totalProperties = 
      (airbnbData?.units?.length ?? 0) + 
      (bookingData?.units?.length ?? 0) + 
      (gathernData?.units?.length ?? 0);
    
    // Connected Properties: count of Msool units
    const connectedProperties = properties.length;
    
    // Number of Channels: unique channels with status === true from Msool units (no duplicates)
    const uniqueChannels = new Set(
      properties.flatMap((p) => 
        (p.channels ?? [])
          .filter((c) => c.status === true)
          .map((c) => c.channelType)
      )
    ).size;
    
    return { totalProperties, connectedProperties, uniqueChannels };
  }, [properties, airbnbData, bookingData, gathernData]);

  // Handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, property: IMsoolUnit) => {
    setMenuAnchor(event.currentTarget);
    setSelectedProperty(property);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedProperty(null);
  }, []);

  const handleAddUnits = useCallback(() => {
    const totalUnmerged = unmergedCounts.airbnb + unmergedCounts.booking + unmergedCounts.gathern;
    
    // Case 1: No unmerged units in any channel
    if (totalUnmerged === 0) {
      setShowNoNewUnitsModal(true);
      return;
    }
    
    // // Case 2: Unmerged units in only one channel - show SelectUnitsModal directly
    // const channelsWithUnits = [
    //   { type: ChannelType.Airbnb, count: unmergedCounts.airbnb },
    //   { type: ChannelType.Booking, count: unmergedCounts.booking },
    //   { type: ChannelType.Gathern, count: unmergedCounts.gathern },
    // ].filter(c => c.count > 0);
    
    // if (channelsWithUnits.length === 1) {
    //   setSelectedChannelType(channelsWithUnits[0].type);
    //   setSelectedProperty(null); // No property selected - adding new unit
    //   setShowAddUnitsModal(true);
    //   return;
    // }
    
    // Case 3: Unmerged units in multiple channels - redirect to mapping page
    navigate('/channels/units-mapping', { state: { isAddingUnits: true } });
  }, [navigate, unmergedCounts]);
  
  const handleCloseNoNewUnitsModal = useCallback(() => {
    setShowNoNewUnitsModal(false);
  }, []);
  
  const handleCloseAddUnitsModal = useCallback(() => {
    setShowAddUnitsModal(false);
    setSelectedChannelType(null);
  }, []);
  
  const handleAddUnitSuccess = useCallback(() => {
    handleCloseAddUnitsModal();
  }, [handleCloseAddUnitsModal]);

  const handleManageMapping = useCallback(() => {
    handleMenuClose();
    navigate('/channels/units-mapping');
  }, [handleMenuClose, navigate]);

  const handleEditProperty = useCallback(() => {
    if (selectedProperty) {
      navigate(`/properties-management/general-settings/${selectedProperty.propertyId}`);
    }
    handleMenuClose();
  }, [selectedProperty, navigate, handleMenuClose]);

  const handleConnectNewChannel = useCallback(() => {
    setShowSelectChannelModal(true);
    setMenuAnchor(null); // Only close menu, keep selectedProperty
  }, []);
  
  const handleCloseSelectChannelModal = useCallback(() => {
    setShowSelectChannelModal(false);
  }, []);
  
  const handleSelectChannel = useCallback((channelType: ChannelType) => {
    setSelectedChannelType(channelType);
    setShowSelectChannelModal(false);
    setShowSelectUnitsModal(true);
  }, []);
  
  const handleCloseSelectUnitsModal = useCallback(() => {
    setShowSelectUnitsModal(false);
    setSelectedChannelType(null);
    setSelectedProperty(null);
  }, []);
  
  const handleLinkUnitSuccess = useCallback(() => {
    handleCloseSelectUnitsModal();
  }, [handleCloseSelectUnitsModal]);

  const handleToggleChannels = useCallback(() => {
    setShowChannelToggleModal(true);
    setMenuAnchor(null); // Only close menu, keep selectedProperty
  }, []);

  const handleCloseChannelToggleModal = useCallback(() => {
    setShowChannelToggleModal(false);
    setSelectedProperty(null);
  }, []);

  const handleUnmerge = useCallback(() => {
    setShowUnmergeModal(true);
    setMenuAnchor(null); // Only close menu, keep selectedProperty
  }, []);

  const handleCloseUnmergeModal = useCallback(() => {
    setShowUnmergeModal(false);
    setSelectedProperty(null);
  }, []);

  const handleUnmergeSuccess = useCallback(() => {
    handleCloseUnmergeModal();
    showToast(t(T_KEYS.UNITS_MAPPING_SUCCESS_UNMAPPED), { severity: 'success' });
  }, [handleCloseUnmergeModal, showToast, t]);

  // Render loading state
  const renderLoading = () => (
    <LoadingContainer>
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rectangular" height={64} sx={{ mb: 2, borderRadius: 2 }} />
      ))}
    </LoadingContainer>
  );

  // Render empty state
  const renderEmpty = () => <PropertiesEmptyState />;

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>{t(T_KEYS.UNITS_MANAGEMENT)}</PageTitle>
        <PageDescription>
          {t(T_KEYS.UNITS_MANAGEMENT_DESC)}
        </PageDescription>
      </HeaderSection>

      <ContentContainer>
        {/* Stats and Add Units Row - hidden in empty state */}
        {properties.length > 0 && (
        <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-9 gap-4 lg:[&>div]:col-span-2 lg:[&>div:first-of-type]:col-span-3 [&>div]:rounded-xl">
          <div
            onClick={handleAddUnits}
            className="w-full bg-[#EEEBFF] p-4 cursor-pointer border border-[#BEB3FF80]"
          >
            <div className="text-lg text-primary mb-2 font-bold">
              {t(T_KEYS.ADD_UNITS)}
            </div>
            <div className="flex justify-between items-center gap-2">
              <div>{t(T_KEYS.ADD_UNITS_DESC)}</div>
              <div className="p-1.5 border-1 bg-[#BEB3FF80] text-primary border-primary border-solid flex rounded-xl shrink-0 aspect-square">
                <AddOutlined />
              </div>
            </div>
          </div>
          <div className="w-full bg-white p-2 xl:p-4 border border-[#EAEDF1]">
            <div className="text-md xl:text-lg mb-2">
              {t(T_KEYS.NUMBER_OF_PROPERTIES)}
            </div>
            <div className="flex justify-between items-center gap-2">
              <div className="text-[24px] xl:text-[36px] text-primary font-bold">
                {String(stats.totalProperties).padStart(2, '0')}
              </div>
              <div className="text-[#EAEDF1] [&>svg]:size-8 flex shrink-0 aspect-square">
                <HomeIcon />
              </div>
            </div>
          </div>
          <div className="w-full bg-white p-2 xl:p-4 border border-[#EAEDF1]">
            <div className="text-md xl:text-lg mb-2">
              {t(T_KEYS.CONNECTED_PROPERTIES)}
            </div>
            <div className="flex justify-between items-center gap-2">
              <div className="text-[24px] xl:text-[36px] text-primary font-bold">
                {String(stats.connectedProperties).padStart(2, '0')}
              </div>
              <div className="text-[#EAEDF1] [&>svg]:size-8 flex shrink-0 aspect-square">
                <LinkedIcon />
              </div>
            </div>
          </div>
          <div className="w-full bg-white p-2 xl:p-4 border border-[#EAEDF1]">
            <div className="text-md xl:text-lg mb-2">
              {t(T_KEYS.NUMBER_OF_CHANNELS)}
            </div>
            <div className="flex justify-between items-center gap-2">
              <div className="text-[24px] xl:text-[36px] text-primary font-bold">
                {String(stats.uniqueChannels).padStart(2, '0')}
              </div>
              <div className="text-[#EAEDF1] [&>svg]:size-8 flex shrink-0 aspect-square">
                <CalendarIcon />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Properties List */}
        {isLoading && renderLoading()}

        {/* Show empty state only when there are no properties at all (not due to search) */}
        {!isLoading && properties.length === 0 && renderEmpty()}

        {/* Show table when properties exist (handles both results and no-results from search) */}
        {!isLoading && properties.length > 0 && (
          <PropertiesTable
            properties={paginatedProperties}
            onMenuOpen={handleMenuOpen}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            totalCount={filteredProperties.length}
          />
        )}
      </ContentContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)',
            minWidth: '200px',
          }
        }}
      >
        <MenuTitle>{t(T_KEYS.PROPERTY_SETTINGS)}</MenuTitle>
        <MenuItem onClick={handleEditProperty}>
          {t(T_KEYS.MANAGE_PROPERTY_DETAILS)}
        </MenuItem>
        <Divider />
        <MenuTitle>{t(T_KEYS.CHANNELS_SETTINGS)}</MenuTitle>
        <MenuItem onClick={handleConnectNewChannel}>
          {t(T_KEYS.CONNECT_NEW_CHANNEL)}
        </MenuItem>
        {/* Hide unmerge if Airbnb is the only connected channel */}
        {selectedProperty?.channels?.some(c => c.status && c.channelType !== ChannelType.Airbnb) && (
          <MenuActionItem onClick={handleUnmerge}>
            {t(T_KEYS.UNMERGE)}
          </MenuActionItem>
        )}
      </Menu>

      {/* Unmerge Options Modal */}
      {selectedProperty && (
        <UnmergeOptionsModal
          open={showUnmergeModal}
          onClose={handleCloseUnmergeModal}
          unit={selectedProperty}
          onSuccess={handleUnmergeSuccess}
        />
      )}

      {/* Channel Toggle Modal */}
      {selectedProperty && (
        <ChannelToggleModal
          open={showChannelToggleModal}
          handleClose={handleCloseChannelToggleModal}
          propertyId={selectedProperty.propertyId}
          propertyName={selectedProperty.propertyName ?? ''}
          channels={selectedProperty.channels}
        />
      )}

      {/* Select Channel Modal */}
      <SelectChannelModal
        open={showSelectChannelModal}
        onClose={handleCloseSelectChannelModal}
        onSelectChannel={handleSelectChannel}
        selectedProperty={selectedProperty}
        unmergedCounts={unmergedCounts}
      />

      {/* Select Units Modal - for linking to existing property */}
      {selectedChannelType && selectedProperty && (
        <SelectUnitsModal
          open={showSelectUnitsModal}
          onClose={handleCloseSelectUnitsModal}
          onSuccess={handleLinkUnitSuccess}
          channelType={selectedChannelType}
          units={selectedChannelUnits}
          propertyId={selectedProperty.propertyId}
          propertyName={selectedProperty.propertyName ?? ''}
        />
      )}

      {/* Add Units Modal - for adding new units without a property */}
      {selectedChannelType && !selectedProperty && (
        <SelectUnitsModal
          open={showAddUnitsModal}
          onClose={handleCloseAddUnitsModal}
          onSuccess={handleAddUnitSuccess}
          channelType={selectedChannelType}
          units={selectedChannelUnits}
        />
      )}

      {/* No New Units Modal */}
      <NoNewUnitsModal
        open={showNoNewUnitsModal}
        onClose={handleCloseNoNewUnitsModal}
      />
    </PageContainer>
  );
};

export default memo(PropertiesListView);
